import React, { useMemo, useState } from "react";
import {
  exerciseLibrary,
  muscleGroupLabels,
  equipmentLabels,
  difficultyLabels,
} from "../data/exerciseLibrary";
import MuscleDiagram from "../components/MuscleDiagram";
import { Search, Timer, Dumbbell, Sparkles, X, Star } from "lucide-react";
import { useFavourites } from "../context/FavouritesContext";
import { toast } from "sonner";

const ALL = "all";

const slugify = (s) => String(s).replace(/\s+/g, "-").toLowerCase();

const FilterRow = ({ label, value, options, labels, onChange, testidPrefix }) => (
  <div className="flex items-center gap-3 flex-wrap">
    <span className="text-[10px] uppercase tracking-widest text-white/40 w-20 shrink-0">
      {label}
    </span>
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          key={opt}
          onClick={() => onChange(opt)}
          className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${
            value === opt
              ? "border-[#6B9BFF] text-[#6B9BFF] bg-[#6B9BFF]/5"
              : "border-white/10 text-white/60 hover:border-white/30"
          }`}
          data-testid={`${testidPrefix}-${slugify(opt)}`}
        >
          {opt === ALL ? "All" : labels[opt] || opt}
        </button>
      ))}
    </div>
  </div>
);

export default function LibraryPage() {
  const [q, setQ] = useState("");
  const [muscleFilter, setMuscleFilter] = useState(ALL);
  const [equipmentFilter, setEquipmentFilter] = useState(ALL);
  const [difficultyFilter, setDifficultyFilter] = useState(ALL);
  const [favOnly, setFavOnly] = useState(false);
  const { isFavourited, toggle: toggleFav } = useFavourites();

  const muscleGroups = useMemo(
    () => [ALL, ...Array.from(new Set(exerciseLibrary.map((e) => e.muscle_group)))],
    []
  );
  const equipmentOptions = useMemo(
    () => [ALL, ...Array.from(new Set(exerciseLibrary.map((e) => e.equipment)))],
    []
  );
  const difficultyOptions = [ALL, "beginner", "intermediate", "advanced"];

  const filtered = useMemo(() => {
    return exerciseLibrary.filter((e) => {
      if (muscleFilter !== ALL && e.muscle_group !== muscleFilter) return false;
      if (equipmentFilter !== ALL && e.equipment !== equipmentFilter) return false;
      if (difficultyFilter !== ALL && e.difficulty !== difficultyFilter) return false;
      if (favOnly && !isFavourited(e.name)) return false;
      if (q && !e.name.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [q, muscleFilter, equipmentFilter, difficultyFilter, favOnly, isFavourited]);

  const total = exerciseLibrary.length;
  const filtersActive =
    muscleFilter !== ALL ||
    equipmentFilter !== ALL ||
    difficultyFilter !== ALL ||
    favOnly ||
    q.length > 0;

  const clearFilters = () => {
    setQ("");
    setMuscleFilter(ALL);
    setEquipmentFilter(ALL);
    setDifficultyFilter(ALL);
    setFavOnly(false);
  };

  const handleStar = async (name) => {
    const nowFav = await toggleFav(name);
    if (nowFav === true) toast.success(`${name} starred`);
    else if (nowFav === false) toast(`${name} unstarred`);
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto fade-up" data-testid="library-page">
      <div className="mb-10">
        <div className="label-eyebrow text-[#6B9BFF] mb-2">Library</div>
        <h1 className="font-display text-4xl sm:text-5xl leading-none">Exercise demos</h1>
        <p className="text-white/60 mt-3 max-w-2xl">
          Browse the move bank, study the technique cues, and tap any card for a clean
          step-by-step breakdown.
        </p>
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search 150+ exercises…"
          className="w-full bg-[#1A1A1A] border border-white/10 pl-10 pr-3 py-3 focus:outline-none focus:border-[#6B9BFF]"
          data-testid="library-search"
        />
      </div>

      {/* Filter rows */}
      <div className="space-y-3 mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest text-white/40 w-20 shrink-0">
            Show
          </span>
          <button
            type="button"
            onClick={() => setFavOnly((v) => !v)}
            className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors inline-flex items-center gap-1.5 ${
              favOnly
                ? "border-[#FFB020] text-[#FFB020] bg-[#FFB020]/5"
                : "border-white/10 text-white/60 hover:border-white/30"
            }`}
            data-testid="library-filter-favourites"
            aria-pressed={favOnly}
          >
            <Star className={`w-3.5 h-3.5 ${favOnly ? "fill-[#FFB020]" : ""}`} />
            {favOnly ? "Favourites only" : "All exercises"}
          </button>
        </div>
        <FilterRow
          label="Muscle"
          value={muscleFilter}
          options={muscleGroups}
          labels={muscleGroupLabels}
          onChange={setMuscleFilter}
          testidPrefix="library-filter"
        />
        <FilterRow
          label="Equipment"
          value={equipmentFilter}
          options={equipmentOptions}
          labels={equipmentLabels}
          onChange={setEquipmentFilter}
          testidPrefix="library-equipment"
        />
        <FilterRow
          label="Level"
          value={difficultyFilter}
          options={difficultyOptions}
          labels={difficultyLabels}
          onChange={setDifficultyFilter}
          testidPrefix="library-difficulty"
        />
      </div>

      {/* Result count */}
      <div className="flex items-center justify-between mb-5">
        <div className="text-xs uppercase tracking-widest text-white/40" data-testid="library-count">
          Showing <span className="text-white font-bold">{filtered.length}</span> of {total}
        </div>
        {filtersActive && (
          <button
            type="button"
            onClick={clearFilters}
            className="text-xs uppercase tracking-widest text-white/50 hover:text-white inline-flex items-center gap-1.5"
            data-testid="library-clear-filters"
          >
            <X className="w-3 h-3" /> Clear filters
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {filtered.map((e, i) => {
          const starred = isFavourited(e.name);
          return (
          <article
            key={`${e.name}-${i}`}
            className="border border-white/10 bg-[#121212] p-5 hover:border-white/25 transition-colors relative"
            data-testid={`library-card-${i}`}
          >
            <button
              type="button"
              onClick={() => handleStar(e.name)}
              className={`absolute top-3 right-3 p-1.5 transition-colors ${
                starred
                  ? "text-[#FFB020] hover:text-[#FFB020]/70"
                  : "text-white/30 hover:text-[#FFB020]"
              }`}
              aria-label={starred ? "Unstar exercise" : "Star exercise"}
              data-testid={`library-star-${i}`}
            >
              <Star className={`w-4 h-4 ${starred ? "fill-[#FFB020]" : ""}`} />
            </button>
            <div className="flex items-start gap-4 pr-8">
              <MuscleDiagram group={e.muscle_group} size={96} />
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-xl leading-tight">{e.name}</h3>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <span className="text-[10px] uppercase tracking-widest text-[#6B9BFF] border border-[#6B9BFF] px-2 py-0.5">
                    {muscleGroupLabels[e.muscle_group] || e.muscle_group}
                  </span>
                  <span className="text-[10px] uppercase tracking-widest text-white/50 border border-white/10 px-2 py-0.5">
                    {equipmentLabels[e.equipment] || e.equipment}
                  </span>
                  <span className="text-[10px] uppercase tracking-widest text-white/40">
                    {e.difficulty}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-2 text-xs text-white/40">
                  {e.is_static_hold && (
                    <span className="inline-flex items-center gap-1">
                      <Timer className="w-3 h-3" /> Static hold
                    </span>
                  )}
                  {e.is_bodyweight && (
                    <span className="inline-flex items-center gap-1">
                      <Dumbbell className="w-3 h-3" /> Bodyweight
                    </span>
                  )}
                </div>
              </div>
            </div>

            <ol className="mt-5 space-y-2 text-sm text-white/80 list-decimal list-inside marker:text-[#6B9BFF] marker:font-bold">
              {e.steps.map((s, j) => (
                <li key={j}>{s}</li>
              ))}
            </ol>

            <div className="mt-4 flex items-start gap-2 text-xs text-white/60 border-t border-white/5 pt-3">
              <Sparkles className="w-3.5 h-3.5 text-[#6B9BFF] shrink-0 mt-0.5" />
              <span>{e.tip}</span>
            </div>
          </article>
          );
        })}

        {filtered.length === 0 && (
          <div
            className="col-span-full text-center py-16 text-white/40 text-sm"
            data-testid="library-empty"
          >
            No exercises match your filters.{" "}
            <button
              type="button"
              onClick={clearFilters}
              className="text-[#6B9BFF] underline hover:text-white"
            >
              Clear all
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
